import React, { useState, useEffect } from 'react';
import { 
  Save, 
  AlertTriangle,
  Shield,
  Database,
  Upload,
  Users,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/authService';

const QuestionsSettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    // Notification Settings
    emailNotifications: true,
    pushNotifications: true,
    notificationFrequency: 'immediate', // immediate, daily, weekly
    autoAssignment: false,
    responseTimeExpectation: 24,
    escalationRules: true,
    
    // Attachment Settings
    attachmentSettings: {
      maxFileSize: 50, // MB
      allowedFileTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'png', 'gif'],
      autoVirusScan: true,
      requireApproval: false,
      maxAttachmentsPerQuestion: 10
    },
    
    // User Management
    userManagement: {
      allowSelfRegistration: false,
      requireEmailVerification: true,
      defaultRole: 'viewer'
    },
    
    // Security Settings
    securitySettings: {
      requireTwoFactor: false,
      sessionTimeout: 480, // minutes
      passwordPolicy: 'medium', // weak, medium, strong
      auditLogging: true
    },
    
    // Data & Privacy Settings
    dataSettings: {
      dataRetentionDays: 365,
      allowDataExport: true,
      anonymizeOldData: false,
      gdprCompliance: true
    }
  });
  const [user, setUser] = useState(null);

  // Load user data
  useEffect(() => {
    const loadUser = async () => {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      }
    };
    loadUser();
  }, []);

  // Check if user has permission to view settings
  if (!user) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Questions Settings</h1>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
            <span className="text-sm text-yellow-700">
              Loading user information...
            </span>
          </div>
        </div>
      </div>
    );
  }

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleNestedSettingChange = (parentKey, childKey, value) => {
    setSettings(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  const handleSaveSettings = () => {
    // Implement save to backend
    console.log('Saving settings:', settings);
    alert('Settings saved successfully!');
  };

  return (
    <div className="p-6">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/adminhq/questions')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Questions Admin
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Questions Module Settings
        </h1>
        <p className="text-gray-600">
          Configure question categories, statuses, and system preferences (Admin Only)
        </p>
      </div>

      {/* Admin notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <Shield className="h-5 w-5 text-blue-600 mr-2" />
          <span className="text-sm text-blue-700">
            <strong>Admin Access:</strong> These settings affect all users of the Questions module.
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Settings</h2>
          
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Email notifications for new questions</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.autoAssignment}
                onChange={(e) => handleSettingChange('autoAssignment', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Auto-assign questions to team members</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.escalationRules}
                onChange={(e) => handleSettingChange('escalationRules', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Enable escalation rules</span>
            </label>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Response time expectation (hours)
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={settings.responseTimeExpectation}
              onChange={(e) => handleSettingChange('responseTimeExpectation', parseInt(e.target.value))}
            />
          </div>
        </div>

        {/* System Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Settings</h2>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Default Question Priority:</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  Medium
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Auto-close after:</span>
                <span className="text-gray-900">30 days of inactivity</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Max file size:</span>
                <span className="text-gray-900">10 MB per attachment</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Upload className="h-5 w-5 mr-2" />
          Attachment Settings
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum file size (MB)
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={settings.attachmentSettings.maxFileSize}
              onChange={(e) => handleNestedSettingChange('attachmentSettings', 'maxFileSize', parseInt(e.target.value))}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max attachments per question
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={settings.attachmentSettings.maxAttachmentsPerQuestion}
              onChange={(e) => handleNestedSettingChange('attachmentSettings', 'maxAttachmentsPerQuestion', parseInt(e.target.value))}
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed file types
            </label>
            <div className="flex flex-wrap gap-2">
              {['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'png', 'gif', 'zip', 'txt'].map(type => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.attachmentSettings.allowedFileTypes.includes(type)}
                    onChange={(e) => {
                      const current = settings.attachmentSettings.allowedFileTypes;
                      const updated = e.target.checked 
                        ? [...current, type]
                        : current.filter(t => t !== type);
                      handleNestedSettingChange('attachmentSettings', 'allowedFileTypes', updated);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{type.toUpperCase()}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="md:col-span-2 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.attachmentSettings.autoVirusScan}
                onChange={(e) => handleNestedSettingChange('attachmentSettings', 'autoVirusScan', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Enable automatic virus scanning</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.attachmentSettings.requireApproval}
                onChange={(e) => handleNestedSettingChange('attachmentSettings', 'requireApproval', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Require approval for large attachments</span>
            </label>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="h-5 w-5 mr-2" />
          Security Settings
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session timeout (minutes)
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={settings.securitySettings.sessionTimeout}
              onChange={(e) => handleNestedSettingChange('securitySettings', 'sessionTimeout', parseInt(e.target.value))}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password policy
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={settings.securitySettings.passwordPolicy}
              onChange={(e) => handleNestedSettingChange('securitySettings', 'passwordPolicy', e.target.value)}
            >
              <option value="weak">Weak (6+ characters)</option>
              <option value="medium">Medium (8+ chars, mixed case)</option>
              <option value="strong">Strong (12+ chars, symbols, numbers)</option>
            </select>
          </div>
          
          <div className="md:col-span-2 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.securitySettings.requireTwoFactor}
                onChange={(e) => handleNestedSettingChange('securitySettings', 'requireTwoFactor', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Require two-factor authentication</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.securitySettings.auditLogging}
                onChange={(e) => handleNestedSettingChange('securitySettings', 'auditLogging', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Enable audit logging</span>
            </label>
          </div>
        </div>
      </div>

      {/* User Management Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2" />
          User Management
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default role for new users
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={settings.userManagement.defaultRole}
              onChange={(e) => handleNestedSettingChange('userManagement', 'defaultRole', e.target.value)}
            >
              <option value="viewer">Viewer</option>
              <option value="support">Support</option>
              <option value="creator">Creator</option>
            </select>
          </div>
          
          <div className="md:col-span-2 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.userManagement.allowSelfRegistration}
                onChange={(e) => handleNestedSettingChange('userManagement', 'allowSelfRegistration', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Allow self-registration</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.userManagement.requireEmailVerification}
                onChange={(e) => handleNestedSettingChange('userManagement', 'requireEmailVerification', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Require email verification</span>
            </label>
          </div>
        </div>
      </div>

      {/* Data & Privacy Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Database className="h-5 w-5 mr-2" />
          Data & Privacy Settings
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data retention period (days)
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={settings.dataSettings.dataRetentionDays}
              onChange={(e) => handleNestedSettingChange('dataSettings', 'dataRetentionDays', parseInt(e.target.value))}
            />
          </div>
          
          <div className="md:col-span-2 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.dataSettings.allowDataExport}
                onChange={(e) => handleNestedSettingChange('dataSettings', 'allowDataExport', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Allow data export</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.dataSettings.gdprCompliance}
                onChange={(e) => handleNestedSettingChange('dataSettings', 'gdprCompliance', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">GDPR compliance mode</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.dataSettings.anonymizeOldData}
                onChange={(e) => handleNestedSettingChange('dataSettings', 'anonymizeOldData', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Anonymize old data automatically</span>
            </label>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Save className="h-4 w-4 mr-2" />
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default QuestionsSettingsPage;

